/*
  ITGC Access Request service — wraps /api/v1/itgc.
*/
import apiClient from './apiService';

export type TargetType = 'APPLICATION' | 'ASSET';
export type LineAction = 'GRANT' | 'REVOKE';
export type LineStatus = 'PENDING' | 'DONE' | 'FAILED';
export type RequestStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'
  | 'FULFILLING' | 'CLOSED' | 'CANCELLED';

export interface AccessLine {
  id: number;
  request_id: number;
  target_user_id: number;
  target_user_name?: string;
  target_type: TargetType;
  target_id: number;
  target_label?: string;
  action: LineAction;
  line_remarks?: string;
  due_date?: string;
  valid_from?: string;
  valid_until?: string;
  line_status: LineStatus;
  fulfilment_proof_id?: number | null;
  fulfilment_remarks?: string;
  fulfilled_on?: string;
  fulfilled_by?: number;
  is_active: number;
  approval_decision?: 'PENDING' | 'APPROVED' | 'REJECTED';
  approval_decided_by?: number;
  approval_decided_on?: string;
  fulfill_status?: 'PENDING' | 'DONE' | 'FAILED';
  fulfill_note?: string;
  fulfilled_date?: string;
  fulfill_done_by?: number;
  fulfill_done_on?: string;
}

export interface AccessLineCreatePayload {
  target_user_id: number;
  target_type: TargetType;
  target_id: number;
  action: LineAction;
  line_remarks?: string;
  due_date?: string;
  valid_from?: string;
  valid_until?: string;
}

export interface AccessRequest {
  id: number;
  entity_id: number;
  request_code?: string;
  title?: string;
  justification?: string;
  status: RequestStatus;
  initiator_user_id: number;
  initiator_name?: string;
  submitted_on?: string;
  verifier_user_id?: number;
  verifier_name?: string;
  verified_on?: string;
  approver_remarks?: string;
  fulfiller_user_id?: number;
  closed_on?: string;
  request_attachment_id?: number;
  is_active: number;
  created_on?: string;
  modified_on?: string;
  lines?: AccessLine[];
}

export interface AccessRequestCreatePayload {
  title?: string;
  justification?: string;
  request_attachment_id?: number;
  lines: AccessLineCreatePayload[];
}

export interface EligibleTargets {
  applications: { id: number; label: string }[];
  assets: { id: number; label: string; sharing_mode: 'SHARED' | 'EXCLUSIVE' }[];
}

export interface UserAclRow {
  id: number;
  entity_id: number;
  user_id: number;
  target_type: TargetType;
  target_id: number;
  target_label?: string;
  granted_on?: string;
  granted_by?: number;
  grant_request_line_id?: number;
  valid_from?: string;
  valid_until?: string;
  revoked_on?: string;
  revoked_by?: number;
  revoke_request_line_id?: number;
  is_active: number;
}

const BASE = '/api/v1/itgc';

export const itgcService = {
  // Requests
  async listRequests(params: {
    page?: number; limit?: number;
    status?: RequestStatus;
    role?: 'mine' | 'to_approve' | 'to_fulfil';
    search?: string;
  } = {}): Promise<{ data: AccessRequest[]; total: number }> {
    const qs: Record<string, string> = {
      page: String(params.page ?? 1),
      limit: String(params.limit ?? 50),
    };
    if (params.status) qs.status = params.status;
    if (params.role) qs.role = params.role;
    if (params.search) qs.search = params.search;
    const r = await apiClient.get(`${BASE}/requests`, { params: qs });
    return { data: r.data?.data ?? [], total: r.data?.meta?.total ?? 0 };
  },
  async getRequest(id: number): Promise<AccessRequest> {
    const r = await apiClient.get(`${BASE}/requests/${id}`);
    return r.data?.data;
  },
  async createRequest(payload: AccessRequestCreatePayload) {
    const r = await apiClient.post(`${BASE}/requests`, payload);
    return r.data;
  },
  async updateRequest(id: number, payload: Partial<Pick<AccessRequest,
    'title' | 'justification' | 'request_attachment_id' | 'is_active'>>) {
    const r = await apiClient.put(`${BASE}/requests/${id}`, payload);
    return r.data;
  },
  async submitRequest(id: number) {
    const r = await apiClient.post(`${BASE}/requests/${id}/submit`);
    return r.data;
  },
  async recallRequest(id: number) {
    const r = await apiClient.post(`${BASE}/requests/${id}/recall`);
    return r.data;
  },
  async cancelRequest(id: number) {
    const r = await apiClient.post(`${BASE}/requests/${id}/cancel`);
    return r.data;
  },
  async approveRequest(id: number, decision: 'APPROVED' | 'REJECTED', approver_remarks?: string) {
    const r = await apiClient.post(`${BASE}/requests/${id}/approve`,
      { decision, approver_remarks });
    return r.data;
  },

  // Eligible targets for a (user, action) — the conditional grant/revoke filter.
  async eligibleTargets(user_id: number, action: LineAction): Promise<EligibleTargets> {
    const r = await apiClient.get(`${BASE}/eligible-targets`,
      { params: { user_id: String(user_id), action } });
    return r.data?.data ?? { applications: [], assets: [] };
  },

  // Lines
  async addLine(request_id: number, payload: AccessLineCreatePayload) {
    const r = await apiClient.post(`${BASE}/requests/${request_id}/lines`, payload);
    return r.data;
  },
  async updateLine(line_id: number, payload: Partial<AccessLineCreatePayload>) {
    const r = await apiClient.put(`${BASE}/lines/${line_id}`, payload);
    return r.data;
  },
  async deleteLine(line_id: number) {
    const r = await apiClient.delete(`${BASE}/lines/${line_id}`);
    return r.data;
  },
  // Soft active/inactive toggle for a saved line (1 = active, 0 = inactive).
  async setLineActive(line_id: number, is_active: 0 | 1) {
    const r = await apiClient.put(`${BASE}/lines/${line_id}`, { is_active });
    return r.data;
  },
  async fulfilLine(line_id: number, payload: {
    line_status: 'DONE' | 'FAILED';
    fulfilment_remarks?: string;
    fulfilment_proof_id?: number;
  }) {
    const r = await apiClient.post(`${BASE}/lines/${line_id}/fulfil`, payload);
    return r.data;
  },
  async approveLines(request_id: number, payload: { approved_line_ids: number[]; approver_note?: string }) {
    const r = await apiClient.post(`${BASE}/requests/${request_id}/approve-lines`, payload);
    return r.data;
  },
  async fulfillLine(line_id: number, payload: { fulfill_note?: string; fulfilled_date: string }) {
    const r = await apiClient.post(`${BASE}/lines/${line_id}/fulfill`, payload);
    return r.data;
  },

  // ACL view
  async listUserAcl(user_id: number, include_revoked = false): Promise<UserAclRow[]> {
    const r = await apiClient.get(`${BASE}/acl/user/${user_id}`,
      { params: { include_revoked: include_revoked ? 'true' : 'false' } });
    return r.data?.data ?? [];
  },
};

export default itgcService;
